import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MailCheck, Send } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-secondary/40">
      <div className="w-full max-w-sm fade-up">
        <div className="mb-8"><BrandLogo size="lg" /></div>
        {sent ? (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <MailCheck className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Check your email</h2>
            <p className="text-muted-foreground text-sm mt-2">
              If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a link to reset your password. The link expires in 1 hour.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary hover:underline font-medium" data-testid="back-to-login-link">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-[0.25em] text-primary font-semibold">Password Reset</p>
            <h2 className="font-display text-3xl font-bold tracking-tight mt-2">Forgot your password?</h2>
            <p className="text-muted-foreground text-sm mt-2">Enter your account email and we'll send you a secure link to set a new one.</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email address</Label>
                <Input
                  id="email" type="email" required data-testid="forgot-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@bolletechnologies.co.za"
                  className="h-11 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-submit-button"
                className="w-full h-11 bg-primary hover:bg-[#C81219] text-white font-semibold transition-colors">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send reset link <Send className="ml-1.5 h-4 w-4" /></>}
              </Button>
            </form>
            <div className="mt-5 text-center">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium" data-testid="back-to-login-link">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
